import React, { useEffect, useState, useMemo } from 'react';
import { X, Copy, Calendar, Folder, Mail, CheckCircle, Clock, FileText, Upload as UploadIcon, Rocket, XCircle, RotateCcw, UserPlus, Edit2, ChevronDown, ChevronRight, Download, Eye, Play, List, Grid3X3, LayoutGrid, Trash2 } from 'lucide-react';
import { Button } from './ui/Button';
import { fileRequestApi, mediaApi, scriptDatabaseApi, getAccessToken } from '../lib/api';
import { formatDate, linkifyText } from '../lib/utils';
import { CanvasBrief3Step } from './CanvasBrief3Step';
import { UploadedFileCard } from './UploadedFileCard';
import { UploadHistoryTimeline } from './UploadHistoryTimeline';
import { ReferenceExistingFilePanel } from './ReferenceExistingFilePanel';
import { ReassignFileRequestModal } from './ReassignFileRequestModal';
import { MarkProgressModal } from './MarkProgressModal';
import { FileRequestOrganizer } from './FileRequestOrganizer';
import { VideoPlayer } from './VideoPlayer';
import { CreateFileRequestModal } from './CreateFileRequestModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { BulkDownloadConfirmModal } from './BulkDownloadConfirmModal';
import { BulkDownloadCodeModal } from './BulkDownloadCodeModal';
import { DownloadCodeModal } from './DownloadCodeModal';
import { useAuth, isAdminRole } from '../contexts/AuthContext';
import { useDownloadProgress } from '../contexts/DownloadProgressContext';
import { getFileRequestStatusColor, getVerticalBadgeClasses } from '../constants/statusColors';

// Canvas Brief type
interface Canvas {
  id?: string;
  file_request_id: string;
  content: any;
  attachments?: any[];
  created_at?: string;
  updated_at?: string;
}

// Status badge helper using centralized colors
function getStatusBadge(status: string | undefined) {
  const statusColor = getFileRequestStatusColor(status);
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor.full}`}>
      {statusColor.label}
    </span>
  );
}

interface FileRequestDetailsModalProps {
  requestId: string;
  onClose: () => void;
  onUpdate: () => void;
  readOnly?: boolean;
}

interface FileUpload {
  id: string;
  file_id: string;
  upload_session_id?: string;
  original_filename: string;
  file_type: string;
  file_size: number;
  thumbnail_url?: string;
  cloudfront_url: string;
  uploaded_by_email?: string;
  uploaded_by_name?: string;
  created_at: string;
  request_folder_id?: string | null;
  request_folder_name?: string | null;
  [key: string]: any;
}

interface AssignedEditor {
  id: string;
  name: string;
  display_name: string;
  assigned_at: string;
  status: string;
  assigned_by_name?: string;
  num_creatives_assigned?: number;
  creatives_completed?: number;
  actual_uploads?: number;
  deliverables_uploaded?: number;
  deliverables_quota?: number;
  reassignment_notes?: string;
}

interface FileRequestDetails {
  id: string;
  title: string;
  description?: string;
  request_type?: string;
  request_token: string;
  is_active: boolean;
  deadline?: string;
  folder_name?: string;
  allow_multiple_uploads: boolean;
  require_email: boolean;
  custom_message?: string;
  upload_count: number;
  num_creatives_requested?: number;
  creator_name?: string;
  creator_email?: string;
  created_by?: string;
  created_at: string;
  assigned_at?: string;
  picked_up_at?: string;
  completed_at?: string;
  delivery_note?: string;
  time_to_complete_hours?: string;
  uploads: FileUpload[];
  assigned_editors?: AssignedEditor[];
  // New status lifecycle fields
  status?: 'open' | 'in_progress' | 'uploaded' | 'launched' | 'closed' | 'reopened';
  uploaded_at?: string;
  uploaded_by?: string;
  launched_at?: string;
  launched_by?: string;
  closed_at?: string;
  closed_by?: string;
  reopened_at?: string;
  reopened_by?: string;
  reopen_count?: number;
  assigned_buyer_id?: string;
  assigned_buyer_ids?: string[];
  assigned_buyers?: { id: string; name: string; email: string }[];
  auto_assigned_head?: string;
  reassignment_count?: number;
  // Fulfillment tracking
  fulfillment?: {
    total_editors: number;
    completed_editors: number;
    in_progress: number;
    pending: number;
    creatives_completed: number;
    creatives_total: number;
    percent: number;
    text: string;
  };
  // Deliverables tracking (main branch feature)
  deliverables_required?: number;
  deliverables_type?: string;
  deliverables?: {
    required: number;
    uploaded: number;
    remaining: number;
    is_complete: boolean;
  };
  // 🆕 Multi-platform/vertical support
  platforms?: string[];
  verticals?: string[];
  platform?: string;  // Backward compatibility
  vertical?: string;  // Backward compatibility
  public_download_link?: string;
  priority?: string;
  priority_set_at?: string;
  priority_set_by?: string;
}

// Canvas file preview card with thumbnail, preview modal, and download
function CanvasFileCard({ file, isImage, isVideo, previewUrl, onDownload }: {
  file: any;
  isImage: boolean;
  isVideo: boolean;
  previewUrl: string | undefined;
  onDownload: (f: any) => void;
}) {
  const [showPreview, setShowPreview] = useState(false);
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);

  const handlePreviewFile = async () => {
    // For videos: we need the actual video file URL, NOT the thumbnail
    if (isVideo) {
      // If we have file_url (actual video), use it directly
      if (file.file_url) {
        setShowPreview(true);
        return;
      }
      // Otherwise fetch the video via media download API
      if (file.file_id && !videoBlobUrl) {
        try {
          const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
          const token = getAccessToken();
          const response = await fetch(`${API_BASE}/media/${file.file_id}/download`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setVideoBlobUrl(url);
          }
        } catch (err) {
          console.error('Failed to fetch video for preview:', err);
        }
      }
      setShowPreview(true);
      return;
    }

    // For images: thumbnail or file_url works fine
    if (isImage && previewUrl) {
      setShowPreview(true);
      return;
    }
    // Fallback: fetch via download API
    if (file.file_id && (isImage || isVideo)) {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const token = getAccessToken();
      const response = await fetch(`${API_BASE}/media/${file.file_id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setShowPreview(true);
        file._blobUrl = url;
      }
    }
  };

  const handleDirectDownload = () => {
    // Download via file_url when file_id is not available
    if (file.file_url) {
      const a = document.createElement('a');
      a.href = file.file_url;
      a.download = file.filename || 'download';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // For videos: use file_url (actual video) or fetched blob, never thumbnail
  // For images: thumbnail or file_url both work
  const displayUrl = isVideo
    ? (videoBlobUrl || file.file_url || file._blobUrl)
    : (file._blobUrl || previewUrl);

  return (
    <>
      <div className="flex items-start gap-3 bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 transition-colors">
        {/* Thumbnail */}
        <div className="flex-shrink-0">
          {isVideo ? (
            <div
              className="w-14 h-14 bg-gray-800 rounded flex items-center justify-center cursor-pointer hover:bg-gray-700 transition-colors"
              onClick={handlePreviewFile}
            >
              {file.thumbnail_url ? (
                <div className="relative w-14 h-14">
                  <img src={file.thumbnail_url} alt="" className="w-14 h-14 object-cover rounded" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded">
                    <Play className="w-6 h-6 text-white" />
                  </div>
                </div>
              ) : (
                <Play className="w-6 h-6 text-white" />
              )}
            </div>
          ) : isImage && (file.thumbnail_url || previewUrl) ? (
            <img
              src={file.thumbnail_url || previewUrl}
              alt={file.filename}
              className="w-14 h-14 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handlePreviewFile}
            />
          ) : (
            <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/40 rounded flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-500" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{file.filename}</p>
          {file.instruction && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{file.instruction}</p>
          )}
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
            {file.type ? file.type.split('/')[1]?.toUpperCase() || file.type : 'File'}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {(isImage || isVideo) && (file.file_id || file.file_url) && (
            <button
              onClick={handlePreviewFile}
              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
              title="Preview"
            >
              <Eye className="w-4 h-4" />
            </button>
          )}
          {file.file_id ? (
            <button
              onClick={() => onDownload({ file_id: file.file_id, original_filename: file.filename } as any)}
              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
              title="Download"
            >
              <Download className="w-4 h-4" />
            </button>
          ) : file.file_url ? (
            <button
              onClick={handleDirectDownload}
              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
              title="Download"
            >
              <Download className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Fullscreen Preview Modal */}
      {showPreview && displayUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[100]"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="relative w-full h-full flex items-center justify-center p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowPreview(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-20 bg-black/50 rounded-full p-2"
            >
              <X className="w-6 h-6" />
            </button>

            {isVideo ? (
              <div className="w-full h-full max-w-[90vw] max-h-[80vh] flex items-center justify-center">
                <VideoPlayer
                  src={displayUrl}
                  poster={file.thumbnail_url}
                  autoPlay={true}
                  className="w-full h-full"
                />
              </div>
            ) : isImage ? (
              <img
                src={displayUrl}
                alt={file.filename}
                className="max-w-full max-h-full rounded-lg object-contain shadow-2xl"
                style={{ maxHeight: '90vh', maxWidth: '90vw' }}
              />
            ) : null}

            <div className="absolute bottom-4 left-4 right-4 bg-black/70 text-white p-4 rounded-lg max-w-2xl mx-auto">
              <p className="font-medium truncate">{file.filename}</p>
              {file.instruction && (
                <p className="text-sm text-gray-300 mt-1">{file.instruction}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function FileRequestDetailsModal({ requestId, onClose, onUpdate, readOnly = false }: FileRequestDetailsModalProps) {
  const { user, featureFlags } = useAuth();
  const { startDownload, isDownloadingFiles } = useDownloadProgress();
  const [request, setRequest] = useState<FileRequestDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [canvas, setCanvas] = useState<Canvas | null>(null);
  const [show3StepCanvas, setShow3StepCanvas] = useState(false);
  const [selectedScriptCode, setSelectedScriptCode] = useState<string | null>(null);
  const [scriptModal, setScriptModal] = useState<any | null>(null);
  const [loadingScript, setLoadingScript] = useState(false);

  // Upload state
  const [showUploadForm, setShowUploadForm] = useState(true); // Show by default for better UX
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadComments, setUploadComments] = useState('');
  const [uploadTargetFolder, setUploadTargetFolder] = useState('');
  const [creativeType, setCreativeType] = useState('');
  const [creativeAngle, setCreativeAngle] = useState('');
  const [scriptNumber, setScriptNumber] = useState('');
  // Per-file real video duration (seconds), keyed by selectedFiles index — drives per-file AI-cost
  const [fileDurations, setFileDurations] = useState<Record<number, string>>({});
  // Per-file editor-entered cost (USD), keyed by selectedFiles index — this is the LOCKED actual cost
  const [fileCosts, setFileCosts] = useState<Record<number, string>>({});
  // Video files whose duration is missing/invalid at upload time — flagged with a red border
  const [invalidDurationIndices, setInvalidDurationIndices] = useState<Set<number>>(new Set());
  // Video files whose cost is missing/invalid at upload time — flagged with a red border
  const [invalidCostIndices, setInvalidCostIndices] = useState<Set<number>>(new Set());
  // LEGACY close flow: uploads exist but predate the per-file cost feature (real_cost_usd is null).
  // Collect a cost per already-delivered file BEFORE launching/closing, then submit it with the call.
  const [costStep, setCostStep] = useState<{ action: 'launch' | 'close' } | null>(null);
  const [legacyFileCosts, setLegacyFileCosts] = useState<Record<string, string>>({});
  const [legacyFileDurations, setLegacyFileDurations] = useState<Record<string, string>>({});
  const [legacyCostError, setLegacyCostError] = useState('');
  const [legacyCostSubmitting, setLegacyCostSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Bulk actions state
  const [selectedUploads, setSelectedUploads] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false);
  // Single + bulk delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<
    | { kind: 'single'; upload: any }
    | { kind: 'bulk'; uploads: any[] }
    | null
  >(null);
  const [downloadStatus, setDownloadStatus] = useState<{ active: boolean; message: string; fileCount: number; type: 'preparing' | 'success' | 'error' }>({ active: false, message: '', fileCount: 0, type: 'preparing' });
  const [downloadCodeFile, setDownloadCodeFile] = useState<FileUpload | null>(null);

  // Deadline editing state
  const [isEditingDeadline, setIsEditingDeadline] = useState(false);
  const [newDeadline, setNewDeadline] = useState('');
  const [savingDeadline, setSavingDeadline] = useState(false);

  // Public download link state
  const [downloadLink, setDownloadLink] = useState('');
  const [isEditingDownloadLink, setIsEditingDownloadLink] = useState(false);
  const [savingDownloadLink, setSavingDownloadLink] = useState(false);

  // Mark Progress modal state
  const [showMarkProgressModal, setShowMarkProgressModal] = useState(false);

  // Reassignment state
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showUploadHistory, setShowUploadHistory] = useState(false);
  const [uploadViewMode, setUploadViewMode] = useState<'list' | 'grid' | 'tile'>('list');
  const [uploadSearchTerm, setUploadSearchTerm] = useState('');
  const [reassignments, setReassignments] = useState<any[]>([]);
  const [expandedReassignments, setExpandedReassignments] = useState<Set<string>>(new Set());

  // Edit history state
  const [editHistory, setEditHistory] = useState<any[]>([]);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedRequest, setEditedRequest] = useState<any>({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Duplicate modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  // Filter uploads by search term
  const filteredUploadsForSearch = useMemo(() => {
    if (!uploadSearchTerm.trim() || !request) return request?.uploads || [];
    const q = uploadSearchTerm.toLowerCase();
    return request.uploads.filter((u: any) =>
      u.original_filename?.toLowerCase().includes(q) ||
      u.request_folder_name?.toLowerCase().includes(q)
    );
  }, [request?.uploads, uploadSearchTerm]);

  useEffect(() => {
    fetchRequestDetails();
    fetchReassignments();
    fetchEditHistory();
  }, [requestId]);

  // Note: scriptNumber should be entered manually by user during upload
  // (canvas_headline field not available in file request data)

  // Subscribe to upload queue to refresh when uploads complete
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const setupQueueSubscription = async () => {
      const { uploadQueueManager } = await import('../services/uploadQueueManager');

      // Seed with already-completed task IDs so we don't re-trigger on old completions
      const seenCompletedIds = new Set<string>(
        uploadQueueManager.getQueue()
          .filter(t => t.requestId === requestId && t.status === 'completed')
          .map(t => t.id)
      );

      unsubscribe = uploadQueueManager.subscribe((queue) => {
        const requestUploads = queue.filter(task => task.requestId === requestId);
        const newlyCompleted = requestUploads.filter(
          task => task.status === 'completed' && !seenCompletedIds.has(task.id)
        );
        newlyCompleted.forEach(task => seenCompletedIds.add(task.id));

        if (newlyCompleted.length > 0) {
          fetchRequestDetails(true);
        }
      });
    };

    setupQueueSubscription();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [requestId]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const items = Array.from(e.dataTransfer.items);
    const files: File[] = [];

    // Process all dropped items (files and folders)
    const processItems = async () => {
      for (const item of items) {
        if (item.kind === 'file') {
          const entry = item.webkitGetAsEntry();
          if (entry) {
            await traverseFileTree(entry, files);
          }
        }
      }
      setSelectedFiles(files);
    };

    processItems();
  };

  const traverseFileTree = async (item: any, files: File[]): Promise<void> => {
    if (item.isFile) {
      return new Promise((resolve) => {
        item.file((file: File) => {
          files.push(file);
          resolve();
        });
      });
    } else if (item.isDirectory) {
      const dirReader = item.createReader();
      // readEntries may not return all entries at once for large directories
      // Keep calling until it returns an empty array
      const readAllEntries = (): Promise<any[]> => {
        return new Promise((resolve) => {
          const allEntries: any[] = [];
          const readBatch = () => {
            dirReader.readEntries((entries: any[]) => {
              if (entries.length === 0) {
                resolve(allEntries);
              } else {
                allEntries.push(...entries);
                readBatch();
              }
            });
          };
          readBatch();
        });
      };
      const entries = await readAllEntries();
      for (const entry of entries) {
        await traverseFileTree(entry, files);
      }
    }
  };

  const fetchRequestDetails = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await fileRequestApi.getOne(requestId);
      setRequest(response.data.data);
      setDownloadLink(response.data.data.public_download_link || '');

      // Try to load canvas
      try {
        const canvasResponse = await fileRequestApi.canvas.get(requestId);
        if (canvasResponse.data.canvas && !canvasResponse.data.isTemplate) {
          setCanvas(canvasResponse.data.canvas);
        } else {
          setCanvas(null); // Explicitly set to null if no canvas exists
        }
      } catch (canvasError: any) {
        // Canvas is optional, ignore error
        setCanvas(null);
      }
    } catch (error: any) {
      console.error('Failed to fetch request details:', error);
      const status = error?.response?.status;
      // Don't show a confusing alert if interceptor already kicked off a logout — the
      // user is about to be redirected to /login anyway.
      if (status === 401) {
        onClose();
        return;
      }
      // Silent background refreshes should never disrupt the UI
      if (silent) return;
      const msg = error?.response?.data?.error
        || (status === 403 ? 'You do not have permission to view this request'
            : status === 404 ? 'This request no longer exists or was deleted'
            : status >= 500 ? 'Server error — please try again in a moment'
            : status === undefined ? 'Network error — check your connection'
            : 'Failed to fetch request details');
      alert(msg);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const getPublicUrl = () => {
    if (!request) return '';
    return `${window.location.origin}/request/${request.request_token}`;
  };

  const handleCopyLink = () => {
    const url = getPublicUrl();
    navigator.clipboard.writeText(url);
    alert('Link copied to clipboard!');
  };

  const performDirectDownload = async (url: string, filename: string) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
      const token = getAccessToken();
      const response = await fetch(fullUrl, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const objUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objUrl);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download file. Please try again.');
    }
  };

  const handleDownload = async (file: FileUpload) => {
    const userRoles = [user?.role, ...((user as any)?.additional_roles || [])].filter(Boolean) as string[];
    const isBuyerLike = userRoles.includes('buyer') && !userRoles.some(r => ['admin','team_lead','ceo','head_media_buying','creative_head','creative','quality_check'].includes(r))
      || (featureFlags?.download_codes_enabled && userRoles.includes('admin'));
    const startDate = featureFlags?.download_codes_start_date ? new Date(featureFlags.download_codes_start_date) : null;
    const eligible = featureFlags?.download_codes_enabled && startDate && new Date((file as any).created_at || Date.now()) >= startDate;

    if (eligible && isBuyerLike) {
      setDownloadCodeFile(file);
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const downloadUrl = `${API_BASE}/media/${file.file_id}/download`;
      const token = getAccessToken();
      const response = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        // Server now returns descriptive JSON body explaining exactly which
        // access checks failed (file owner / editor / buyer / requester / QC
        // / admin). Surface that to the editor so they don't see a generic
        // "Failed to download" with no idea what to do.
        let serverMsg = '';
        try {
          const body = await response.json();
          serverMsg = body && body.error ? String(body.error) : '';
        } catch (_) { /* not JSON */ }
        if (response.status === 403 && serverMsg) {
          alert(serverMsg);
          return;
        }
        if (response.status === 404) {
          alert(serverMsg || 'File not found or has been deleted.');
          return;
        }
        throw new Error(serverMsg || `Download failed (HTTP ${response.status})`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      alert(error && error.message ? error.message : 'Failed to download file. Please try again.');
    }
  };

  const handleAddToLibrary = async (upload: FileUpload) => {
    try {
      // Add file to media library (makes it visible in the target folder)
      await mediaApi.addFileRequestUploadToLibrary(upload.file_id);
      alert(`"${upload.original_filename}" has been added to your Media Library in the target folder!`);

      // Refresh the request details to update the upload list
      await fetchRequestDetails();
    } catch (error: any) {
      console.error('Failed to add to library:', error);
      alert(error.response?.data?.error || 'Failed to add file to Media Library');
    }
  };

  const handleRemoveFromRequest = (upload: FileUpload) => {
    if (!upload.upload_session_id) {
      alert('This upload cannot be removed (missing upload session id).');
      return;
    }
    // Open the modal — actual delete happens in confirmDeleteSingle below
    setDeleteModal({ kind: 'single', upload });
  };

  const confirmDeleteSingle = async () => {
    if (!deleteModal || deleteModal.kind !== 'single') return;
    const upload = deleteModal.upload;
    setBulkActionInProgress(true);
    try {
      await fileRequestApi.deleteUploadSession(requestId, upload.upload_session_id);
      setDeleteModal(null);
      await fetchRequestDetails();
    } catch (error: any) {
      console.error('Failed to remove upload from request:', error);
      alert(error.response?.data?.error || 'Failed to remove upload');
    } finally {
      setBulkActionInProgress(false);
    }
  };

  const handleRename = async (upload: FileUpload, newFilename: string) => {
    try {
      await mediaApi.rename(upload.file_id, newFilename);
      // Refresh the request details to show updated filename everywhere
      await fetchRequestDetails();
    } catch (error: any) {
      console.error('Failed to rename file:', error);
      throw new Error(error.response?.data?.error || 'Failed to rename file');
    }
  };

  // Bulk selection handlers (supports Shift+click range selection)
  const toggleSelectUpload = (uploadId: string, shiftKey?: boolean) => {
    if (!request) return;
    const uploads = request.uploads;
    const clickedIndex = uploads.findIndex(u => u.id === uploadId);

    if (shiftKey && lastSelectedIndex !== null && clickedIndex !== -1 && clickedIndex !== lastSelectedIndex) {
      // Shift+click: select range between last click and current click
      const start = Math.min(lastSelectedIndex, clickedIndex);
      const end = Math.max(lastSelectedIndex, clickedIndex);
      const newSelected = new Set(selectedUploads);
      for (let i = start; i <= end; i++) {
        newSelected.add(uploads[i].id);
      }
      setSelectedUploads(newSelected);
    } else {
      // Normal click: toggle single item
      const newSelected = new Set(selectedUploads);
      if (newSelected.has(uploadId)) {
        newSelected.delete(uploadId);
      } else {
        newSelected.add(uploadId);
      }
      setSelectedUploads(newSelected);
    }
    setLastSelectedIndex(clickedIndex);
  };

  const toggleReassignment = (reassignmentId: string) => {
    const newExpanded = new Set(expandedReassignments);
    if (newExpanded.has(reassignmentId)) {
      newExpanded.delete(reassignmentId);
    } else {
      newExpanded.add(reassignmentId);
    }
    setExpandedReassignments(newExpanded);
  };

  const toggleSelectAll = () => {
    if (selectedUploads.size === request?.uploads.length) {
      setSelectedUploads(new Set());
    } else {
      setSelectedUploads(new Set(request?.uploads.map(u => u.id) || []));
    }
  };

  // Bulk download is now a 2-step process:
  //   1. user clicks "Download Selected" → opens BulkDownloadConfirmModal
  //      (shows file count + total size + ETA)
  //   2. user clicks Download in modal → DownloadProgressContext.startDownload()
  //      streams the ZIP with a bottom-right progress card
  //
  // The context dedups concurrent jobs for the same file set, so repeated
  // clicks don't spawn parallel ZIP builds (which crashed Render on May 4).
  const [bulkDownloadModalState, setBulkDownloadModalState] = useState<{
    isOpen: boolean;
    fileIds: string[];
    folderMap: Record<string, string>;
    label: string;
  }>({ isOpen: false, fileIds: [], folderMap: {}, label: '' });

  const [bulkCodeModalState, setBulkCodeModalState] = useState<{
    isOpen: boolean;
    files: { file_id: string; original_filename: string }[];
    folderMap: Record<string, string>;
    label: string;
  }>({ isOpen: false, files: [], folderMap: {}, label: '' });

  const handleBulkDownload = () => {
    if (!request || selectedUploads.size === 0) return;
    const uploadsToDownload = request.uploads.filter(u => selectedUploads.has(u.id));
    const fileIds = uploadsToDownload.map(u => u.file_id).filter(Boolean) as string[];
    if (fileIds.length === 0) return;

    // Single-file: just trigger direct download (no ZIP needed)
    if (fileIds.length === 1) {
      handleDownload(uploadsToDownload[0]);
      return;
    }

    const folderMap: Record<string, string> = {};
    uploadsToDownload.forEach(u => {
      if (u.file_id && u.request_folder_name) {
        folderMap[u.file_id] = u.request_folder_name;
      }
    });

    const userRoles = [user?.role, ...((user as any)?.additional_roles || [])].filter(Boolean) as string[];
    const isBuyerLike = (userRoles.includes('buyer') && !userRoles.some(r => ['admin','team_lead','ceo','head_media_buying','creative_head','creative','quality_check'].includes(r)))
      || (featureFlags?.download_codes_enabled && userRoles.includes('admin'));
    const startDate = featureFlags?.download_codes_start_date ? new Date(featureFlags.download_codes_start_date) : null;
    const eligible = featureFlags?.download_codes_enabled && startDate && isBuyerLike;

    if (eligible) {
      setBulkCodeModalState({
        isOpen: true,
        files: uploadsToDownload.map(u => ({ file_id: u.file_id, original_filename: u.original_filename })),
        folderMap,
        label: `${fileIds.length} files from "${request.title}"`,
      });
      return;
    }

    setBulkDownloadModalState({
      isOpen: true,
      fileIds,
      folderMap,
      label: `${fileIds.length} files from "${request.title}"`,
    });
  };

  const handleBulkAddToLibrary = async () => {
    if (!request || selectedUploads.size === 0) return;

    setBulkActionInProgress(true);
    try {
      const uploadsToAdd = request.uploads.filter(u => selectedUploads.has(u.id));

      // Add each file to library (makes them visible in their target folders)
      const results = await Promise.allSettled(
        uploadsToAdd.map(upload =>
          mediaApi.addFileRequestUploadToLibrary(upload.file_id)
        )
      );

      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (failed > 0) {
        alert(`Added ${successful} file(s) to Media Library in their target folders. ${failed} file(s) failed.`);
      } else {
        alert(`Successfully added ${successful} file(s) to your Media Library in their target folders!`);
      }

      // Clear selection and refresh to show updated status
      setSelectedUploads(new Set());
      await fetchRequestDetails();
    } catch (error: any) {
      console.error('Bulk add to library error:', error);
      alert('Failed to add files to Media Library. Please try again.');
    } finally {
      setBulkActionInProgress(false);
    }
  };

  const handleBulkRemoveFromRequest = () => {
    if (!request || selectedUploads.size === 0) return;
    const uploadsToRemove = request.uploads.filter(u => selectedUploads.has(u.id) && u.upload_session_id);
    if (uploadsToRemove.length === 0) return;
    // Open the typed-confirmation modal — actual delete happens in confirmDeleteBulk
    setDeleteModal({ kind: 'bulk', uploads: uploadsToRemove });
  };

  const confirmDeleteBulk = async () => {
    if (!deleteModal || deleteModal.kind !== 'bulk') return;
    const uploadsToRemove = deleteModal.uploads;
    setBulkActionInProgress(true);
    try {
      const results = await Promise.allSettled(
        uploadsToRemove.map(upload =>
          fileRequestApi.deleteUploadSession(requestId, upload.upload_session_id!)
        )
      );
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed > 0) alert(`Removed ${successful} file(s). ${failed} file(s) failed to remove.`);
      else alert(`Successfully removed ${successful} file(s) from the request.`);
      setSelectedUploads(new Set());
      setDeleteModal(null);
      await fetchRequestDetails();
    } catch (error: any) {
      console.error('Bulk remove error:', error);
      alert('Failed to remove files. Please try again.');
    } finally {
      setBulkActionInProgress(false);
    }
  };

  const handleSaveDeadline = async () => {
    if (!request || !newDeadline) return;

    setSavingDeadline(true);
    try {
      await fileRequestApi.update(request.id, { deadline: newDeadline });
      alert('Deadline updated successfully!');
      setIsEditingDeadline(false);
      fetchRequestDetails(); // Refresh to show new deadline
    } catch (error: any) {
      console.error('Failed to update deadline:', error);
      alert(error.response?.data?.error || 'Failed to update deadline');
    } finally {
      setSavingDeadline(false);
    }
  };

  const handleCancelDeadlineEdit = () => {
    setIsEditingDeadline(false);
    setNewDeadline('');
  };

  const handleSaveDownloadLink = async () => {
    if (!request) return;

    setSavingDownloadLink(true);
    try {
      await fileRequestApi.update(request.id, { public_download_link: downloadLink.trim() });
      setIsEditingDownloadLink(false);
      fetchRequestDetails(true);
    } catch (error: any) {
      console.error('Failed to update download link:', error);
      alert(error.response?.data?.error || 'Failed to update download link');
    } finally {
      setSavingDownloadLink(false);
    }
  };

  const handleCopyDownloadLink = () => {
    if (downloadLink) {
      navigator.clipboard.writeText(downloadLink);
      alert('Download link copied!');
    }
  };

  const formatDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      const remainingHours = diffHours % 24;
      return `${diffDays}d ${remainingHours}h`;
    } else if (diffHours > 0) {
      const remainingMins = diffMins % 60;
      return `${diffHours}h ${remainingMins}m`;
    } else {
      return `${diffMins}m`;
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedFiles.length === 0) {
      setUploadError('Please select at least one file');
      return;
    }

    // Every file needs a cost (a number >= 0, so 0 is allowed but must be entered); every VIDEO
    // additionally needs a duration (> 0). Images: cost only.
    const isVideoFile = (file: File) =>
      (file.type || '').startsWith('video/') || /\.(mp4|mov|webm|avi|mkv|m4v|wmv|flv)$/i.test(file.name);
    const isBlankCost = (i: number) => {
      const c = fileCosts[i];
      return c === undefined || String(c).trim() === '' || !(Number(c) >= 0);
    };
    const missingDuration = new Set<number>();
    const missingCost = new Set<number>();
    selectedFiles.forEach((file, i) => {
      if (isBlankCost(i)) missingCost.add(i);
      if (isVideoFile(file) && !(Number(fileDurations[i]) > 0)) missingDuration.add(i);
    });
    if (missingDuration.size > 0 || missingCost.size > 0) {
      setInvalidDurationIndices(missingDuration);
      setInvalidCostIndices(missingCost);
      setUploadError('Enter a cost for every file (and a duration for every video) before uploading.');
      return;
    }
    setInvalidDurationIndices(new Set());
    setInvalidCostIndices(new Set());

    setUploading(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      // Import upload queue manager dynamically
      const { uploadQueueManager } = await import('../services/uploadQueueManager');

      // Add files to upload queue with creative metadata for UCODE generation
      const taskIds = uploadQueueManager.addToQueue(
        selectedFiles,
        requestId,
        uploadComments || undefined,
        uploadTargetFolder.trim() || undefined,
        {
          creative_type: creativeType || undefined,
          creative_angle: creativeAngle || undefined,
          script_number: scriptNumber || undefined,
          // Each file's own duration (undefined for images / blanks) — aligned to selectedFiles order
          durations: selectedFiles.map((_, i) => {
            const n = Number(fileDurations[i]);
            return n > 0 ? n : undefined;
          }),
          // Each file's own editor-entered cost — the LOCKED actual cost, aligned to selectedFiles order
          // (0 is a valid cost and is preserved; only blank/invalid becomes undefined = "not entered").
          costs: selectedFiles.map((_, i) => {
            const c = fileCosts[i];
            if (c === undefined || String(c).trim() === '') return undefined;
            const n = Number(c);
            return Number.isFinite(n) && n >= 0 ? n : undefined;
          }),
        }
      );

      console.log(`Added ${selectedFiles.length} files to upload queue`, taskIds);

      // Show success message
      setUploadSuccess(true);
      setSelectedFiles([]);
      setFileDurations({});
      setFileCosts({});
      setInvalidDurationIndices(new Set());
      setInvalidCostIndices(new Set());
      setUploadComments('');
      setUploadTargetFolder('');
      setCreativeType('');
      setCreativeAngle('');

      // Close form and refresh once after short delay (no flickering)
      setTimeout(() => {
        setUploadSuccess(false);
        setShowUploadForm(false);
        fetchRequestDetails();
        // Don't call onUpdate() here - it causes double refresh/flicker
        // The parent will see updated data when the modal closes
      }, 1500);
    } catch (error: any) {
      console.error('Upload failed:', error);
      setUploadError(error.response?.data?.error || 'Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Status transition handlers
  const handleMarkAsUploaded = async () => {
    if (!window.confirm('Mark this request as uploaded? This indicates that all files have been uploaded.')) return;

    // Editors already enter a cost per file at upload, so the request's real cost is known — don't
    // re-prompt. Only ask when NO uploaded file carries a cost (real_cost_usd is null); otherwise the
    // backend derives the total from the per-file costs automatically.
    let payload: { actual_cost_usd?: number } = {};
    if ((request as any)?.real_cost_usd == null) {
      const raw = window.prompt(
        'Total cost incurred on the videos/images you generated for this request (USD).\n' +
        'Enter 0 if nothing was spent.',
        '0'
      );
      if (raw === null) return;   // cancelled — do not deliver
      const cost = Number(raw.trim());
      if (!Number.isFinite(cost) || cost < 0) {
        alert('Cost must be a non-negative number.');
        return;
      }
      payload = { actual_cost_usd: cost };
    }

    try {
      await fileRequestApi.markAsUploaded(requestId, payload);
      await fetchRequestDetails();
      onUpdate();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to mark as uploaded');
    }
  };

  // A request needs the legacy cost step when it has delivered uploads but no per-file cost was ever
  // captured (real_cost_usd is null). Modern requests (cost present) close with no prompt.
  const needsLegacyCost = () =>
    (request as any)?.real_cost_usd == null && (request?.uploads?.length || 0) > 0;

  const isVideoUpload = (u: any) =>
    String(u?.file_type || '').toLowerCase() === 'video' ||
    /\.(mp4|mov|webm|avi|mkv|m4v|wmv|flv)$/i.test(String(u?.original_filename || ''));

  const handleLaunch = async () => {
    if (needsLegacyCost()) { setCostStep({ action: 'launch' }); return; }
    if (!window.confirm('Launch this request? This indicates acceptance of the uploaded files.')) return;

    try {
      await fileRequestApi.launch(requestId);
      await fetchRequestDetails();
      onUpdate();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to launch request');
    }
  };

  const handleClose = async () => {
    if (needsLegacyCost()) { setCostStep({ action: 'close' }); return; }
    if (!window.confirm('Close this request? You can reopen it later if needed.')) return;

    try {
      await fileRequestApi.closeRequest(requestId);
      await fetchRequestDetails();
      onUpdate();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to close request');
    }
  };

  // Submit the per-file costs entered in the legacy cost step, then run the pending launch/close.
  const handleSubmitLegacyCost = async () => {
    if (!costStep || !request) return;
    setLegacyCostError('');

    const uploads = request.uploads || [];
    const file_costs: Array<{ media_file_id: string; cost_usd: number; duration_seconds?: number }> = [];
    for (const u of uploads) {
      const costRaw = (legacyFileCosts[u.id] ?? '').trim();
      const cost = Number(costRaw);
      if (costRaw === '' || !Number.isFinite(cost) || cost < 0) {
        setLegacyCostError('Enter a cost (USD) for every file. Enter 0 if nothing was spent.');
        return;
      }
      const entry: { media_file_id: string; cost_usd: number; duration_seconds?: number } = {
        media_file_id: u.id,
        cost_usd: Math.round(cost * 10000) / 10000,
      };
      // Video with no stored duration also needs a duration.
      if (isVideoUpload(u) && !(Number((u as any).duration) > 0)) {
        const durRaw = (legacyFileDurations[u.id] ?? '').trim();
        const dur = Number(durRaw);
        if (durRaw === '' || !Number.isFinite(dur) || dur <= 0) {
          setLegacyCostError('Enter the duration (seconds) for every video missing one.');
          return;
        }
        entry.duration_seconds = dur;
      }
      file_costs.push(entry);
    }

    try {
      setLegacyCostSubmitting(true);
      if (costStep.action === 'launch') {
        await fileRequestApi.launch(requestId, { file_costs });
      } else {
        await fileRequestApi.closeRequest(requestId, { file_costs });
      }
      setCostStep(null);
      setLegacyFileCosts({});
      setLegacyFileDurations({});
      await fetchRequestDetails();
      onUpdate();
    } catch (error: any) {
      setLegacyCostError(error.response?.data?.error || `Failed to ${costStep.action} request`);
    } finally {
      setLegacyCostSubmitting(false);
    }
  };

  const handleReopen = async () => {
    if (!window.confirm('Reopen this closed request?')) return;

    try {
      await fileRequestApi.reopenRequest(requestId);
      await fetchRequestDetails();
      onUpdate();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to reopen request');
    }
  };

  const fetchReassignments = async () => {
    try {
      const response = await fileRequestApi.getReassignments(requestId);
      setReassignments(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to fetch reassignments:', error);
    }
  };

  const fetchEditHistory = async () => {
    try {
      const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/file-requests/${requestId}/edit-history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      setEditHistory(result.data || []);
    } catch (error: any) {
      console.error('Failed to fetch edit history:', error);
    }
  };

  const handleReassignSuccess = async () => {
    setShowReassignModal(false);
    await fetchRequestDetails();
    await fetchReassignments();
    onUpdate();
  };

  const handleEditRequest = () => {
    if (!request) return;
    setIsEditMode(true);
    setEditedRequest({
      title: request.title,
      concept_notes: request.description || '',
      num_creatives: request.num_creatives_requested || 0,
      request_type: request.request_type,
      deadline: request.deadline,
      platforms: request.platforms || [],
      verticals: request.verticals || []
    });
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedRequest({});
  };

  const handlePriorityChange = async (newPriority: string) => {
    try {
      const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/file-requests/${requestId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority: newPriority, edit_reason: 'Priority updated' })
      });
      if (!response.ok) throw new Error('Failed to update priority');
      fetchRequestDetails();
    } catch (error) {
      alert('Failed to update priority. Please try again.');
    }
  };

  const handleSaveEdit = async () => {
    try {
      setIsSavingEdit(true);
      const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
      const token = getAccessToken();

      const response = await fetch(`${API_BASE_URL}/file-requests/${requestId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...editedRequest,
          edit_reason: 'Updated request details'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update request');
      }

      alert('Request updated successfully!');
      setIsEditMode(false);
      fetchRequestDetails();
      fetchEditHistory();
    } catch (error) {
      console.error('Error updating request:', error);
      alert('Failed to update request. Please try again.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleSave3StepCanvas = async (content: string, files: File[]) => {
    try {
      const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
      const token = getAccessToken();

      // Parse the content string back to object for API
      const contentObj = JSON.parse(content);

      // Step 1: Save the canvas brief metadata
      const canvasSaveResponse = await fetch(`${API_BASE_URL}/file-requests/${requestId}/canvas`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: contentObj })
      });

      const canvasSaveData = await canvasSaveResponse.json();

      if (!canvasSaveResponse.ok) {
        throw new Error(canvasSaveData.error || 'Failed to save canvas');
      }

      // Step 2: Upload reference files to "canvas brief reference" folder
      if (files.length > 0) {
        // Count how many samples were in the content before adding new files
        // This helps us know which samples in the array are the newly uploaded ones
        const samplesBeforeUpload = (contentObj.samples || []).length - files.length;

        // First, find or create the "canvas brief reference" folder
        const foldersResponse = await fetch(`${API_BASE_URL}/folders`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const foldersData = await foldersResponse.json();

        let canvasFolderId = foldersData.data?.find((f: any) =>
          f.name.toLowerCase() === 'canvas brief reference'
        )?.id;

        // If folder doesn't exist, create it
        if (!canvasFolderId) {
          const createFolderResponse = await fetch(`${API_BASE_URL}/folders`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ name: 'Canvas Brief Reference' })
          });
          const createdFolder = await createFolderResponse.json();
          canvasFolderId = createdFolder.data.id;
        }

        // Get a valid editor_id (for canvas briefs, we need to find an active editor)
        // If user is a creative, get their editor ID, otherwise use the first assigned editor
        let editorId = '';
        if (user?.role === 'creative') {
          // Get editor profile for creative user
          try {
            const editorResponse = await fetch(`${API_BASE_URL}/editors`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const editorsData = await editorResponse.json();
            const userEditor = editorsData.data?.find((e: any) => e.user_id === user.id);
            if (userEditor) {
              editorId = userEditor.id;
            }
          } catch (err) {
            console.error('Failed to get editor:', err);
          }
        }

        // If still no editor_id, use the first assigned editor from the request
        if (!editorId && request?.assigned_editors && request.assigned_editors.length > 0) {
          editorId = request.assigned_editors[0].id;
        }

        // If still no editor, get any active editor as fallback
        if (!editorId) {
          try {
            const editorResponse = await fetch(`${API_BASE_URL}/editors`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const editorsData = await editorResponse.json();
            const firstEditor = editorsData.data?.find((e: any) => e.is_active);
            if (firstEditor) {
              editorId = firstEditor.id;
            }
          } catch (err) {
            console.error('Failed to get fallback editor:', err);
          }
        }

        // Create folder structure: Canvas Brief Reference / [Request Title]
        // Similar to file requests, each request gets its own subfolder
        const requestFolderName = request?.title || `Request ${requestId.slice(0, 8)}`;

        // Fetch all folders to find existing request folder
        const allFoldersResponse = await fetch(`${API_BASE_URL}/folders`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const allFolders = await allFoldersResponse.json();

        // Find or create request-specific folder as child of Canvas Brief Reference folder
        const existingRequestFolder = allFolders.data?.find((f: any) =>
          f.name === requestFolderName && f.parent_id === canvasFolderId
        );

        let requestFolderId: string;
        if (existingRequestFolder) {
          requestFolderId = existingRequestFolder.id;
        } else {
          // Create request folder
          const createRequestFolderResponse = await fetch(`${API_BASE_URL}/folders`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              name: requestFolderName,
              parent_id: canvasFolderId
            })
          });
          const createdRequestFolder = await createRequestFolderResponse.json();
          requestFolderId = createdRequestFolder.data.id;
        }

        // Create "Creatives" subfolder inside request folder for canvas brief uploads
        const existingCreativeFolder = allFolders.data?.find((f: any) =>
          f.name === 'Creatives' && f.parent_id === requestFolderId
        );

        let creativeFolderId: string;
        if (existingCreativeFolder) {
          creativeFolderId = existingCreativeFolder.id;
        } else {
          const createCreativeFolderResponse = await fetch(`${API_BASE_URL}/folders`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              name: 'Creatives',
              parent_id: requestFolderId
            })
          });
          const createdCreativeFolder = await createCreativeFolderResponse.json();
          creativeFolderId = createdCreativeFolder.data.id;
        }

        // Upload files and collect their IDs + URLs
        const uploadedFiles: { id: string; file_url: string; thumbnail_url: string }[] = [];

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const instruction = contentObj.samples[samplesBeforeUpload + i]?.instruction || '';

          const formData = new FormData();
          formData.append('file', file);
          formData.append('editor_id', editorId);
          formData.append('folder_id', creativeFolderId);  // Upload to Creatives subfolder
          formData.append('description', `Canvas Brief - ${request?.title || 'Request'}: ${instruction}`);
          formData.append('tags', JSON.stringify(['canvas-brief', `request-${requestId}`]));

          const uploadResponse = await fetch(`${API_BASE_URL}/media/upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          if (!uploadResponse.ok) {
            const errorData = await uploadResponse.json();
            console.error('Upload failed:', errorData);
            throw new Error(`Failed to upload ${file.name}: ${errorData.error || 'Unknown error'}`);
          }

          const uploadData = await uploadResponse.json();
          // Response shape: { success: true, data: { id, s3_url, thumbnail_url, ... } }
          const uploadedFile = uploadData.data;
          if (!uploadedFile?.id) {
            throw new Error(`Upload response missing file id for ${file.name}`);
          }
          uploadedFiles.push({
            id: uploadedFile.id,
            file_url: uploadedFile.s3_url || uploadedFile.file_url || '',
            thumbnail_url: uploadedFile.thumbnail_url || '',
          });
        }

        // Update canvas samples with file IDs, URLs, and thumbnails
        if (uploadedFiles.length > 0) {

          const updatedContent = { ...contentObj };
          // Ensure samples array exists
          if (!updatedContent.samples) {
            updatedContent.samples = [];
          }
          // Attach file_id + file_url + thumbnail_url to the newly uploaded samples
          for (let i = 0; i < uploadedFiles.length; i++) {
            const sampleIndex = samplesBeforeUpload + i;
            const uf = uploadedFiles[i];
            if (updatedContent.samples[sampleIndex]) {
              updatedContent.samples[sampleIndex].file_id = uf.id;
              updatedContent.samples[sampleIndex].file_url = uf.file_url;
              updatedContent.samples[sampleIndex].thumbnail_url = uf.thumbnail_url;
            }
          }

          // Update canvas with file IDs
          const updateResponse = await fetch(`${API_BASE_URL}/file-requests/${requestId}/canvas`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ content: updatedContent })
          });

          if (!updateResponse.ok) {
            const errorData = await updateResponse.json();
            console.warn('Canvas file-id update failed (files already uploaded):', errorData);
            // Don't throw - files are already uploaded
          }
        }
      }

      alert('Canvas Brief saved successfully!');
      setShow3StepCanvas(false);
      fetchRequestDetails(); // Refresh to show updated canvas
    } catch (error: any) {
      console.error('Error saving canvas:', error);
      alert(`Failed to save canvas: ${error.message || 'Please try again.'}`);
    }
  };

  const handleDuplicateRequest = () => {
    // Open create modal pre-filled with current request data
    setShowDuplicateModal(true);
  };

  // Determine if user can reassign - admins, creative heads, vertical heads, and ATLs
  const userRole = user?.role as string;
  const userAdditionalRoles: string[] = (user as any)?.additional_roles || [];
  const hasAdminRole = isAdminRole(userRole, userAdditionalRoles);
  const isVH = userRole === 'vertical_head' || userAdditionalRoles.includes('vertical_head');
  const isATL = userRole === 'assistant_team_lead' || userAdditionalRoles.includes('assistant_team_lead');
  const isAutoAssignedHead = request?.auto_assigned_head === user?.id;

  const canReassign = !readOnly && user && request && (
    hasAdminRole ||
    isVH ||
    isATL ||
    isAutoAssignedHead
  );

  // Debug logging for admin role permission issues
  if (user?.role === 'admin' || user?.role === 'ceo') {
    console.debug('[FileRequestDetailsModal] Admin Permission Check:', {
      userId: user?.id,
      userRole,
      userAdditionalRoles,
      hasAdminRole,
      canReassign,
      timestamp: new Date().toISOString()
    });
  }

  // 3-hour edit lock for buyers - admins can always edit
  // readOnly mode locks everything
  const isEditLocked = readOnly || (() => {
    if (!request || !user) return false;
    if (isAdminRole(user.role, (user as any)?.additional_roles)) return false;
    if (user.role !== 'buyer') return true;
    const createdAt = new Date(request.created_at);
    const threeHoursLater = new Date(createdAt.getTime() + 3 * 60 * 60 * 1000);
    return new Date() > threeHoursLater;
  })();

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          <p className="text-gray-700 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (!request) return null;

  // Helper: Render headline with clickable script codes
  const renderHeadlineWithScriptCodes = (text: string) => {
    // Pattern: 2-4 uppercase letters, dash, S, digits, optionally -v#/-STATE_CODE
    // Examples: HI-S3, CA-S1-v2, TX-S3-CA
    const scriptCodePattern = /([A-Z]{2,4}-S\d+(?:-v\d+|-[A-Z]{2})?)/g;
    const parts = text.split(scriptCodePattern);

    return parts.map((part, idx) => {
      if (scriptCodePattern.test(part)) {
        return (
          <button
            key={idx}
            onClick={async () => {
              setSelectedScriptCode(part);
              setLoadingScript(true);
              try {
                const res = await scriptDatabaseApi.getAll({ search: part });
                if (res.data.data.length > 0) {
                  setScriptModal(res.data.data[0]); // Get first match
                } else {
                  alert('Script not found');
                }
              } catch (err) {
                alert('Failed to load script');
              } finally {
                setLoadingScript(false);
              }
            }}
            className="font-bold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
            title="Click to view script"
          >
            {part}
          </button>
        );
      }
      return part === '' ? null : <span key={idx}>{linkifyText(part)}</span>;
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {request.title}
              </h2>
              {getStatusBadge(request.status)}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {request.deliverables || request.fulfillment ? (
                <span className="space-y-0.5 block">
                  {request.deliverables && (
                    <span className="block">
                      Deliverables: {request.deliverables.uploaded}/{request.deliverables.required}
                      {request.deliverables.remaining > 0 ? ` • ${request.deliverables.remaining} remaining` : ''}
                      {request.deliverables.is_complete ? ' (complete)' : ''}
                    </span>
                  )}
                  {request.fulfillment && (
                    <span className="block">
                      Creatives: {request.fulfillment.text} completed ({request.fulfillment.percent}%)
                      {request.fulfillment.total_editors ? ` • ${request.fulfillment.completed_editors}/${request.fulfillment.total_editors} editors done` : ''}
                    </span>
                  )}
                </span>
              ) : (
                <span>
                  {request.upload_count} {request.upload_count === 1 ? 'file' : 'files'} uploaded
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isEditMode ? (
              <>
                {/* Save Button */}
                <button
                  onClick={handleSaveEdit}
                  disabled={isSavingEdit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isSavingEdit ? 'Saving...' : 'Save'}
                </button>
                {/* Cancel Button */}
                <button
                  onClick={handleCancelEdit}
                  disabled={isSavingEdit}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                {/* Edit Button - Only for buyers (within 3hrs) and admins */}
                {(user?.role === 'buyer' || isAdminRole(user?.role, (user as any)?.additional_roles)) &&
                 request.status !== 'closed' && request.status !== 'launched' && (
                  isEditLocked ? (
                    <span
                      className="p-2 text-gray-400 cursor-not-allowed rounded-lg"
                      title="Editing locked after 3 hours. Contact admin to edit."
                    >
                      <Edit2 className="w-4 h-4" />
                    </span>
                  ) : (
                    <button
                      onClick={() => handleEditRequest()}
                      className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Edit Request"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )
                )}

                {/* Copy Link Button */}
                {request?.request_token && (
                  <button
                    onClick={() => {
                      const baseUrl = window.location.origin;
                      const link = `${baseUrl}/upload/${request.request_token}`;
                      navigator.clipboard.writeText(link);
                      alert('Upload link copied to clipboard!');
                    }}
                    className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 rounded-lg transition-colors"
                    title="Copy Upload Link"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              </>
            )}

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Request Summary - Key Information */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">Request Summary</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {/* Media Buyer */}
              {request.creator_name && (
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Requested By:</span>
                  <p className="text-blue-900 dark:text-blue-100">{request.creator_name}</p>
                  {request.creator_email && (
                    <p className="text-xs text-blue-600 dark:text-blue-400">{request.creator_email}</p>
                  )}
                </div>
              )}

              {/* Assigned Buyers */}
              {request.assigned_buyers && request.assigned_buyers.length > 0 && (
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Assigned Buyer(s):</span>
                  <div className="mt-0.5 space-y-0.5">
                    {request.assigned_buyers.map((buyer) => (
                      <p key={buyer.id} className="text-blue-900 dark:text-blue-100 text-xs">
                        {buyer.name} <span className="text-blue-600 dark:text-blue-400">({buyer.email})</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Number of Creatives */}
              {(request.num_creatives_requested !== undefined && request.num_creatives_requested > 0) || isEditMode ? (
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Creatives Requested:</span>
                  {isEditMode ? (
                    <input
                      type="number"
                      value={editedRequest.num_creatives || ''}
                      onChange={(e) => setEditedRequest({ ...editedRequest, num_creatives: parseInt(e.target.value) || 0 })}
                      className="mt-1 w-full px-2 py-1 text-sm border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-gray-800 text-blue-900 dark:text-blue-100"
                      min="1"
                    />
                  ) : (
                    <p className="text-blue-900 dark:text-blue-100">{request.num_creatives_requested}</p>
                  )}
                </div>
              ) : null}

              {/* Priority */}
              {(() => {
                const canSetPriority = hasAdminRole || isVH || isATL;
                const priorityColors: Record<string, string> = {
                  urgent: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
                  high:   'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
                  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
                  low:    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
                };
                const p = request.priority || 'low';
                if (!canSetPriority && !request.priority) return null;
                return (
                  <div>
                    <span className="text-blue-700 dark:text-blue-300 font-medium">Priority:</span>
                    {canSetPriority ? (
                      <select
                        value={p}
                        onChange={(e) => handlePriorityChange(e.target.value)}
                        className={`mt-1 w-full px-2 py-1 text-sm border border-blue-300 dark:border-blue-700 rounded font-semibold cursor-pointer ${priorityColors[p] || ''}`}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    ) : (
                      <span className={`mt-1 inline-block px-2 py-0.5 text-xs rounded-full font-semibold capitalize ${priorityColors[p] || ''}`}>{p}</span>
                    )}
                  </div>
                );
              })()}

              {/* Request Type */}
              {(request.request_type || isEditMode) && (
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Type:</span>
                  {isEditMode ? (
                    <select
                      value={editedRequest.request_type || ''}
                      onChange={(e) => setEditedRequest({ ...editedRequest, request_type: e.target.value })}
                      className="mt-1 w-full px-2 py-1 text-sm border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-gray-800 text-blue-900 dark:text-blue-100"
                    >
                      <option value="UGC">UGC</option>
                      <option value="Special Request">Special Request</option>
                      <option value="Static">Static</option>
                      <option value="Video">Video</option>
                    </select>
                  ) : (
                    <p className="text-blue-900 dark:text-blue-100 capitalize">{request.request_type}</p>
                  )}
                </div>
              )}

              {/* Platforms */}
              {(request.platforms && request.platforms.length > 0) || request.platform ? (
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Platforms:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(request.platforms || (request.platform ? [request.platform] : [])).map((platform, idx) => (
                      <span key={idx} className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-blue-200 text-blue-900 dark:bg-blue-800 dark:text-blue-100">
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Verticals */}
              {(request.verticals && request.verticals.length > 0) || request.vertical ? (
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Verticals:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(request.verticals || (request.vertical ? [request.vertical] : [])).map((vertical, idx) => (
                      <span key={idx} className={getVerticalBadgeClasses(vertical)}>
                        {vertical}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Created Date */}
              <div>
                <span className="text-blue-700 dark:text-blue-300 font-medium">Created:</span>
                <p className="text-blue-900 dark:text-blue-100">{formatDate(request.created_at)}</p>
              </div>

              {/* Completed Date */}
              {request.completed_at && (
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Completed:</span>
                  <p className="text-blue-900 dark:text-blue-100">{formatDate(request.completed_at)}</p>
                </div>
              )}

              {/* Total Time to Complete */}
              {request.time_to_complete_hours && (
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Time to Complete:</span>
                  <p className="text-blue-900 dark:text-blue-100 font-semibold">{request.time_to_complete_hours} hours</p>
                </div>
              )}

              {/* Assigned To (Editors) */}
              {request.assigned_editors && request.assigned_editors.length > 0 && (
                <div className="col-span-2">
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Assigned To:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {request.assigned_editors.map((editor) => (
                      <div key={editor.id} className="inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded">
                        <span className="text-sm text-blue-900 dark:text-blue-100">
                          {editor.display_name || editor.name}
                        </span>
                        {editor.status && editor.status !== 'pending' && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            editor.status === 'completed' ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200' :
                            editor.status === 'in_progress' || editor.status === 'accepted' ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200' :
                            editor.status === 'reassigned' ? 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200' :
                            'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}>
                            {editor.status}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status Action Buttons — hidden in readOnly mode */}
          {user && !readOnly && (
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Actions</h3>
              <div className="flex flex-wrap gap-2">
                {/* 1. Mark as Uploaded - Only for assigned editors when status is open/in_progress */}
                {request.assigned_editors?.some(e => e.id === user.id) &&
                  (request.status === 'open' || request.status === 'in_progress') && (
                  <Button
                    onClick={handleMarkAsUploaded}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Uploaded
                  </Button>
                )}

                {/* 1b. Manual Mark Progress - VH, ATL, Team Lead, Admin, Developer */}
                {(() => {
                  const additionalRoles = (user as any).additional_roles || [];
                  const isVHPlus = isAdminRole(user.role, (user as any)?.additional_roles) ||
                    ['team_lead', 'developer', 'super_admin'].includes(user.role) ||
                    additionalRoles.some((r: string) => ['vertical_head', 'assistant_team_lead', 'team_lead', 'admin'].includes(r));
                  if (!isVHPlus || request.status === 'closed') return null;
                  return (
                    <Button
                      onClick={() => setShowMarkProgressModal(true)}
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Mark Progress
                    </Button>
                  );
                })()}

                {/* 1c. Sync Editor Statuses - Admin only, fixes stuck in_progress rows */}
                {isAdminRole(user?.role, (user as any)?.additional_roles) && (
                  <Button
                    onClick={async () => {
                      try {
                        await fileRequestApi.syncEditorStatuses(requestId);
                        await fetchRequestDetails(true);
                        alert('Editor statuses synced successfully.');
                      } catch (e: any) {
                        alert(e?.response?.data?.error || 'Sync failed');
                      }
                    }}
                    size="sm"
                    variant="outline"
                  >
                    Sync Statuses
                  </Button>
                )}

                {/* 2. Launch - For creator/assigned buyer when status is uploaded */}
                {(request.created_by === user.id || request.assigned_buyer_id === user.id || request.assigned_buyer_ids?.includes(user.id) || isAdminRole(user.role, (user as any)?.additional_roles)) &&
                  request.status === 'uploaded' && (
                  <Button
                    onClick={handleLaunch}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    Launch
                  </Button>
                )}

                {/* 3. Close - For creator/buyer/admin when not already closed */}
                {(request.created_by === user.id || request.assigned_buyer_id === user.id || request.assigned_buyer_ids?.includes(user.id) || isAdminRole(user.role, (user as any)?.additional_roles)) &&
                  request.status !== 'closed' && request.status !== 'launched' && (
                  <Button
                    onClick={handleClose}
                    size="sm"
                    variant="outline"
                    className="border-gray-400 text-gray-600 hover:bg-gray-100 dark:border-gray-500 dark:text-gray-400"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Close
                  </Button>
                )}

                {/* 4. Reopen - For creator/assigned buyer when status is closed */}
                {(request.created_by === user.id || request.assigned_buyer_id === user.id || request.assigned_buyer_ids?.includes(user.id) || isAdminRole(user.role, (user as any)?.additional_roles)) &&
                  request.status === 'closed' && (
                  <Button
                    onClick={handleReopen}
                    size="sm"
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reopen Request
                  </Button>
                )}

                {/* 5. Reassign */}
                {canReassign && request.status !== 'closed' && (
                  <Button
                    onClick={() => setShowReassignModal(true)}
                    size="sm"
                    variant="outline"
                    className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Reassign
                  </Button>
                )}

                {/* 6. Duplicate Request - Opens create modal pre-filled */}
                {(user?.role === 'buyer' || isAdminRole(user?.role, (user as any)?.additional_roles)) && (
                  <Button
                    onClick={handleDuplicateRequest}
                    size="sm"
                    variant="outline"
                    className="border-purple-400 text-purple-600 hover:bg-purple-50 dark:border-purple-500 dark:text-purple-400"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Duplicate Request
                  </Button>
                )}

                {/* 7. Reset Assignments - Admin/creative_head only */}
                {hasAdminRole && reassignments.length > 3 && request.status !== 'closed' && (
                  <Button
                    onClick={async () => {
                      if (!window.confirm(
                        `Reset ALL assignments for "${request.title}"?\n\n` +
                        `This will:\n` +
                        `• Remove all ${request.assigned_editors?.length || 0} editor assignments\n` +
                        `• Clear all ${reassignments.length} reassignment history entries\n` +
                        `• Set request status back to "open"\n\n` +
                        `You'll need to reassign editors from scratch. This cannot be undone.`
                      )) return;
                      try {
                        await fileRequestApi.resetAssignments(requestId);
                        alert('Assignments reset successfully. Request is now open for fresh assignment.');
                        onUpdate?.();
                      } catch (err: any) {
                        alert(err.response?.data?.error || 'Failed to reset assignments');
                      }
                    }}
                    size="sm"
                    variant="outline"
                    className="border-red-400 text-red-600 hover:bg-red-50 dark:border-red-500 dark:text-red-400"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset Assignments
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Reassignment History */}
          {reassignments.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Reassignment History ({reassignments.length} {reassignments.length === 1 ? 'change' : 'changes'})
              </h4>

              {/* Current Assignments Section */}
              {request.assigned_editors && request.assigned_editors.length > 0 && (
                <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-3">
                  <p className="text-xs font-semibold text-blue-900 dark:text-blue-100 mb-2">
                    Currently Assigned:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {request.assigned_editors.map((editor: any) => (
                      <span
                        key={editor.id}
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          editor.status === 'reassigned'
                            ? 'bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-100'
                            : 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100'
                        }`}
                      >
                        {editor.display_name || editor.name}
                        {editor.status && (
                          <span className="opacity-70">({editor.status})</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* History Timeline - Accordion Style */}
              <div className="space-y-2">
                {reassignments.map((r: any, index: number) => {
                  const isExpanded = expandedReassignments.has(r.id);

                  return (
                    <div
                      key={r.id}
                      className="border rounded-lg overflow-hidden transition-colors border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                    >
                      {/* Accordion Header - Always visible */}
                      <button
                        onClick={() => toggleReassignment(r.id)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {/* Expand/Collapse Icon */}
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}

                          {/* Reassignment Summary */}
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-700 dark:text-gray-300">{r.from_name}</span>
                            {r.progress_from_total > 0 && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                {r.progress_from_completed}/{r.progress_from_total}
                              </span>
                            )}
                            <span className="text-gray-500 dark:text-gray-400">→</span>
                            <span className="font-medium text-blue-600 dark:text-blue-400">{r.to_name}</span>
                            {r.progress_to_total > 0 && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-700 text-blue-600 dark:text-blue-200">
                                0/{r.progress_to_total}
                              </span>
                            )}
                          </div>
                        </div>

                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(r.created_at)}
                        </span>
                      </button>

                      {/* Accordion Content - Expandable */}
                      {isExpanded && (r.reassignment_note || r.editor_note || r.progress_from_total > 0) && (
                        <div className="px-4 pb-4 pt-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 space-y-2">
                          {r.progress_from_total > 0 && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded px-3 py-2 border border-gray-200 dark:border-gray-700">
                              <span className="font-medium text-gray-700 dark:text-gray-300">Progress at reassignment: </span>
                              <span>{r.progress_from_completed}/{r.progress_from_total} creatives completed</span>
                              {r.progress_from_total > 0 && (
                                <span className="ml-2 text-gray-500">
                                  ({Math.round((r.progress_from_completed / r.progress_from_total) * 100)}%)
                                </span>
                              )}
                            </div>
                          )}
                          {r.reassignment_note && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded px-3 py-2 border border-gray-200 dark:border-gray-700">
                              <span className="font-medium text-gray-700 dark:text-gray-300">Note: </span>
                              <span className="whitespace-pre-wrap">{r.reassignment_note}</span>
                            </div>
                          )}
                          {r.editor_note && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded px-3 py-2 border border-blue-200 dark:border-blue-700">
                              <span className="font-medium text-blue-700 dark:text-blue-300">Per-Editor Note: </span>
                              <span className="whitespace-pre-wrap">{r.editor_note}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Canvas Brief */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Canvas Brief
              </h3>
              <div className="flex items-center gap-2">
                {canvas?.created_at && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Created: {formatDate(canvas.created_at)}
                  </span>
                )}
                {canvas && (user?.role === 'buyer' || isAdminRole(user?.role, (user as any)?.additional_roles)) && (
                  <Button variant="outline" size="sm" onClick={() => setShow3StepCanvas(true)}>
                    <FileText className="w-4 h-4 mr-1" /> Edit
                  </Button>
                )}
                {canvas && user?.role === 'creative' && (
                  <Button variant="outline" size="sm" onClick={() => setShow3StepCanvas(true)}>
                    <FileText className="w-4 h-4 mr-1" /> View Full
                  </Button>
                )}
                {!canvas && (user?.role === 'buyer' || isAdminRole(user?.role, (user as any)?.additional_roles)) && (
                  <Button variant="outline" size="sm" onClick={() => setShow3StepCanvas(true)}>
                    <FileText className="w-4 h-4 mr-1" /> Create
                  </Button>
                )}
              </div>
            </div>

            {/* Canvas Brief Content - visible to ALL roles */}
            {canvas && (() => {
              try {
                const content = typeof canvas.content === 'string' ? JSON.parse(canvas.content) : canvas.content;
                return (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    {content.headline && (
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1">HEADLINE</h4>
                        <p className="text-sm text-blue-900 dark:text-blue-100">
                          {renderHeadlineWithScriptCodes(content.headline)}
                        </p>
                      </div>
                    )}
                    {content.script && (
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1">SCRIPT</h4>
                        <p className="text-sm text-blue-900 dark:text-blue-100 whitespace-pre-wrap">{linkifyText(content.script)}</p>
                      </div>
                    )}
                    {/* Combined reference samples + attachments as rich file cards */}
                    {(() => {
                      // Merge samples and attachments into one list for unified display
                      const allFiles: any[] = [];
                      if (content.samples) {
                        content.samples.forEach((s: any) => allFiles.push({
                          file_id: s.file_id,
                          filename: s.filename,
                          instruction: s.instruction,
                          type: s.type || '',
                          file_url: s.file_url,
                          thumbnail_url: s.thumbnail_url,
                          source: 'sample',
                        }));
                      }
                      if (canvas.attachments) {
                        canvas.attachments.forEach((a: any) => {
                          // Avoid duplicates if attachment file_id matches a sample
                          const isDuplicate = allFiles.some(f => f.file_id && f.file_id === a.file_id);
                          if (!isDuplicate) {
                            allFiles.push({
                              file_id: a.file_id,
                              filename: a.file_name || a.filename || 'Attachment',
                              type: a.file_type || '',
                              file_url: a.file_url,
                              thumbnail_url: a.thumbnail_url,
                              source: 'attachment',
                            });
                          } else {
                            // Merge thumbnail/url from attachment into existing sample
                            const existing = allFiles.find(f => f.file_id === a.file_id);
                            if (existing) {
                              if (!existing.thumbnail_url && a.thumbnail_url) existing.thumbnail_url = a.thumbnail_url;
                              if (!existing.file_url && a.file_url) existing.file_url = a.file_url;
                              if (!existing.type && a.file_type) existing.type = a.file_type;
                            }
                          }
                        });
                      }

                      if (allFiles.length === 0) return null;

                      const isImageType = (type: string, name: string) =>
                        type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(name || '');
                      const isVideoType = (type: string, name: string) =>
                        type?.startsWith('video/') || /\.(mp4|mov|avi|webm|mkv)$/i.test(name || '');

                      return (
                        <div>
                          <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-2">
                            REFERENCE FILES ({allFiles.length})
                          </h4>
                          <div className="space-y-2">
                            {allFiles.map((file: any, idx: number) => {
                              const isImage = isImageType(file.type, file.filename);
                              const isVideo = isVideoType(file.type, file.filename);
                              const previewUrl = file.thumbnail_url || file.file_url;

                              return (
                                <CanvasFileCard
                                  key={idx}
                                  file={file}
                                  isImage={isImage}
                                  isVideo={isVideo}
                                  previewUrl={previewUrl}
                                  onDownload={handleDownload}
                                />
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              } catch (e) {
                console.error('Failed to parse canvas content:', e);
                return null;
              }
            })()}
          </div>

          {/* Request Info */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-3">
            {(request.description || isEditMode) && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </h3>
                {isEditMode ? (
                  <textarea
                    value={editedRequest.concept_notes || ''}
                    onChange={(e) => setEditedRequest({ ...editedRequest, concept_notes: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    rows={4}
                    placeholder="Enter request description..."
                  />
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{linkifyText(request.description || '')}</p>
                )}
              </div>
            )}

            {request.custom_message && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Custom Message
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{linkifyText(request.custom_message)}</p>
              </div>
            )}

            {request.assigned_editors && request.assigned_editors.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Assigned Creatives ({request.num_creatives_requested || request.assigned_editors.filter(e => e.status !== 'reassigned' && e.status !== 'removed').length})
                </h3>
                <div className="grid grid-cols-4 gap-3">
                  {request.assigned_editors.filter(e => e.status !== 'reassigned' && e.status !== 'removed').map((editor) => {
                    const completed = editor.creatives_completed || 0;
                    const total = editor.num_creatives_assigned || 0;
                    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
                    const status = total === 0 ? 'none' : completed >= total ? 'completed' : completed > 0 ? 'progress' : 'pending';
                    const strokeColor = status === 'completed' ? '#16a34a' : status === 'progress' ? '#d97706' : '#9ca3af';
                    const r = 18;
                    const circ = 2 * Math.PI * r;

                    return (
                      <div key={editor.id} className="flex flex-col items-center text-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        {/* Donut chart */}
                        {total > 0 ? (
                          <svg width="48" height="48" viewBox="0 0 48 48" className="mb-1">
                            <circle cx="24" cy="24" r={r} fill="none" stroke="#e5e7eb" strokeWidth="4" />
                            <circle cx="24" cy="24" r={r} fill="none" stroke={strokeColor} strokeWidth="4"
                              strokeDasharray={circ} strokeDashoffset={circ - (pct / 100) * circ}
                              strokeLinecap="round" transform="rotate(-90 24 24)" className="transition-all" />
                            <text x="24" y="24" textAnchor="middle" dominantBaseline="central"
                              className="fill-gray-700 dark:fill-gray-300" fontSize="11" fontWeight="600">
                              {pct}%
                            </text>
                          </svg>
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-1">
                            <span className="text-xs text-gray-400">--</span>
                          </div>
                        )}
                        {/* Progress below chart */}
                        {total > 0 && (
                          <span className={`text-[10px] font-medium ${status === 'completed' ? 'text-green-600 dark:text-green-400' : status === 'progress' ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-500'}`}>
                            {completed}/{total}
                          </span>
                        )}
                        {/* Name */}
                        <span className="text-xs font-medium text-gray-900 dark:text-white truncate w-full mt-0.5">
                          {editor.display_name || editor.name}
                        </span>
                        {/* Status badge */}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full mt-0.5 ${
                          status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : status === 'progress' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {status === 'completed' ? 'Done' : status === 'progress' ? 'In Progress' : 'Pending'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {request.delivery_note && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Delivery Note
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{request.delivery_note}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-gray-700 dark:text-gray-300">
                  Created: {formatDate(request.created_at)}
                </span>
                {user?.role === 'buyer' && (
                  isEditLocked ? (
                    <span className="text-xs text-red-500 font-medium" title="Editing locked after 3 hours of creation">Editing Locked</span>
                  ) : (
                    <span className="text-xs text-green-600 font-medium">Editable</span>
                  )
                )}
              </div>

              {request.request_type && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-700 dark:text-gray-300">
                    Type: <span className="font-medium capitalize">{request.request_type}</span>
                  </span>
                </div>
              )}

              {request.folder_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Folder className="w-4 h-4 text-muted-foreground" />
                  <span className="text-gray-700 dark:text-gray-300">{request.folder_name}</span>
                </div>
              )}

              {/* Deadline Section with Edit Capability */}
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                {isEditingDeadline ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={newDeadline}
                      onChange={(e) => setNewDeadline(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <Button
                      size="sm"
                      onClick={handleSaveDeadline}
                      disabled={savingDeadline || !newDeadline}
                    >
                      {savingDeadline ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelDeadlineEdit}
                      disabled={savingDeadline}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 dark:text-gray-300">
                      Deadline: {request.deadline ? formatDate(request.deadline) : 'Not set'}
                    </span>
                    {user?.role !== 'creative' && (
                      <button
                        onClick={() => {
                          setIsEditingDeadline(true);
                          setNewDeadline(request.deadline || '');
                        }}
                        className="text-blue-600 hover:text-blue-800 text-xs underline"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                )}
              </div>

              {request.require_email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-gray-700 dark:text-gray-300">Email required</span>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-muted-foreground" />
                <span className="text-gray-700 dark:text-gray-300">
                  {request.allow_multiple_uploads ? 'Multiple uploads allowed' : 'Single upload only'}
                </span>
              </div>

              {request.picked_up_at && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Picked up: {formatDate(request.picked_up_at)}
                  </span>
                </div>
              )}

              {request.completed_at && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Completed: {formatDate(request.completed_at)}
                  </span>
                </div>
              )}

              {request.launched_at && (
                <div className="flex items-center gap-2 text-sm">
                  <Rocket className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Launched: {formatDate(request.launched_at)}
                  </span>
                </div>
              )}

              {request.closed_at && (
                <div className="flex items-center gap-2 text-sm">
                  <XCircle className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Closed: {formatDate(request.closed_at)}
                  </span>
                </div>
              )}

              {request.reopened_at && (
                <div className="flex items-center gap-2 text-sm">
                  <RotateCcw className="w-4 h-4 text-orange-500" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Reopened: {formatDate(request.reopened_at)} {request.reopen_count ? `(${request.reopen_count} times)` : ''}
                  </span>
                </div>
              )}

              {request.picked_up_at && request.completed_at && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span className="text-gray-700 dark:text-gray-300 font-medium">
                    Duration: {formatDuration(request.picked_up_at, request.completed_at)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Public Link */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Public Upload Link
            </h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={getPublicUrl()}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 text-sm"
              />
              <Button onClick={handleCopyLink}>
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>

          {/* Public Download Link */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Public Download Link
            </h3>
            {isEditingDownloadLink ? (
              <div className="flex gap-2">
                <input
                  type="url"
                  value={downloadLink}
                  onChange={(e) => setDownloadLink(e.target.value)}
                  placeholder="Paste download link (Google Drive, Dropbox, etc.)"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={savingDownloadLink}
                />
                <Button
                  size="sm"
                  onClick={handleSaveDownloadLink}
                  disabled={savingDownloadLink}
                >
                  {savingDownloadLink ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsEditingDownloadLink(false);
                    setDownloadLink(request.public_download_link || '');
                  }}
                  disabled={savingDownloadLink}
                >
                  Cancel
                </Button>
              </div>
            ) : downloadLink ? (
              <div className="flex gap-2">
                <a
                  href={downloadLink.match(/^https?:\/\//) ? downloadLink : `https://${downloadLink}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-3 py-2 border border-green-300 dark:border-green-700 rounded-md bg-green-50 dark:bg-green-900/20 text-sm text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30 truncate flex items-center gap-2"
                >
                  <Download className="w-4 h-4 flex-shrink-0" />
                  {downloadLink}
                </a>
                <Button size="sm" variant="outline" onClick={handleCopyDownloadLink}>
                  <Copy className="w-4 h-4" />
                </Button>
                {!readOnly && user?.role !== 'buyer' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsEditingDownloadLink(true)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400 italic flex-1">
                  {user?.role === 'buyer' ? 'No download link provided yet' : 'No download link set'}
                </span>
                {!readOnly && user?.role !== 'buyer' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsEditingDownloadLink(true)}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Add Link
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Direct Upload for Editors - Only editors/creatives can upload, not buyers. Hidden in readOnly mode. */}
          {!readOnly && request.is_active && user?.role !== 'buyer' && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Upload Files {request.allow_multiple_uploads && '(Multiple files/folders allowed)'}
              </h3>

              {showUploadForm && (
                <form onSubmit={handleFileUpload} className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  {/* File Selection with Drag & Drop */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select Files or Folder * (Multiple files/folders allowed)
                    </label>

                    {/* Drag & Drop Zone */}
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`relative space-y-2 p-4 border-2 border-dashed rounded-lg transition-colors ${
                        isDragging
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {isDragging && (
                        <div className="absolute inset-0 flex items-center justify-center bg-blue-50/90 dark:bg-blue-900/40 rounded-lg pointer-events-none z-10">
                          <div className="text-center">
                            <UploadIcon className="w-12 h-12 mx-auto mb-2 text-blue-600 dark:text-blue-400" />
                            <p className="text-lg font-medium text-blue-700 dark:text-blue-300">
                              Drop files or folders here
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Regular file upload */}
                      <input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          setSelectedFiles(prev => [...prev, ...files]);
                          e.target.value = '';
                        }}
                        className="w-full text-sm text-gray-700 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-200"
                        disabled={uploading}
                      />

                      {/* Folder upload - supports adding multiple folders */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="file"
                          // @ts-ignore - webkitdirectory is not in TypeScript types but works in all modern browsers
                          webkitdirectory="true"
                          directory="true"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length > 0) {
                              setSelectedFiles(prev => [...prev, ...files]);
                              e.target.value = '';
                            }
                          }}
                          className="hidden"
                          id="folder-upload-input"
                          disabled={uploading}
                        />
                        <label
                          htmlFor="folder-upload-input"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900 dark:text-green-200 cursor-pointer"
                        >
                          + Add Folder
                        </label>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Click multiple times to add several folders
                        </span>
                        {selectedFiles.length > 0 && (
                          <span className="text-xs text-gray-500">
                            ({(() => {
                              const folders = new Set(selectedFiles.map(f => (f as any).webkitRelativePath?.split('/')[0]).filter(Boolean));
                              return folders.size > 0 ? `${folders.size} folder(s), ${selectedFiles.length} files` : `${selectedFiles.length} files`;
                            })()})
                          </span>
                        )}
                      </div>

                      {/* Drag & Drop Hint */}
                      {!isDragging && selectedFiles.length === 0 && (
                        <div className="text-center py-2 text-sm text-gray-500 dark:text-gray-400">
                          <UploadIcon className="w-8 h-8 mx-auto mb-1 opacity-50" />
                          <p>Or drag and drop files/folders here</p>
                        </div>
                      )}

                      {/* Reference an existing file instead of re-uploading */}
                      <ReferenceExistingFilePanel
                        requestId={requestId}
                        onAttached={() => fetchRequestDetails(true)}
                      />
                    </div>
                    {selectedFiles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Selected {selectedFiles.length} file(s)
                            {(() => {
                              const totalSize = selectedFiles.reduce((s, f) => s + f.size, 0);
                              return ` (${(totalSize / 1024 / 1024).toFixed(1)} MB total)`;
                            })()}:
                          </p>
                          <button type="button" onClick={() => { setSelectedFiles([]); setFileDurations({}); setFileCosts({}); setInvalidDurationIndices(new Set()); setInvalidCostIndices(new Set()); }} className="text-xs text-red-500 hover:text-red-700">Clear All</button>
                        </div>
                        <div className="max-h-40 overflow-y-auto space-y-0.5">
                          {selectedFiles.map((file, idx) => {
                            const relativePath = (file as any).webkitRelativePath;
                            const isVideo = (file.type || '').startsWith('video/') || /\.(mp4|mov|webm|avi|mkv|m4v|wmv|flv)$/i.test(file.name);
                            const durStr = fileDurations[idx] || '';
                            const costStr = fileCosts[idx] || '';
                            return (
                              <div key={idx} className="flex items-center justify-between gap-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                                <span className="truncate min-w-0" title={relativePath || file.name}>
                                  {relativePath ? <span className="text-gray-400">{relativePath.split('/').slice(0, -1).join('/')}/</span> : null}
                                  {file.name}
                                </span>
                                <div className="flex items-center gap-2 ml-2 whitespace-nowrap">
                                  {isVideo && (
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={durStr}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setFileDurations(prev => ({ ...prev, [idx]: val }));
                                          const n = Number(val);
                                          if (n > 0) {
                                            setInvalidDurationIndices(prev => {
                                              if (!prev.has(idx)) return prev;
                                              const next = new Set(prev);
                                              next.delete(idx);
                                              return next;
                                            });
                                            // Convenience: prefill cost from the rate card ONLY when the
                                            // editor hasn't typed a cost yet. Their value stays authoritative.
                                            setFileCosts(prev => {
                                              if (prev[idx] && prev[idx].trim() !== '') return prev;
                                              return { ...prev, [idx]: (n * 0.059 + 0.02).toFixed(2) };
                                            });
                                          }
                                        }}
                                        required
                                        disabled={uploading}
                                        title="Duration in seconds"
                                        className={`w-24 px-1.5 py-0.5 text-xs border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 ${invalidDurationIndices.has(idx) ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'}`}
                                      />
                                      <span className="text-xs text-gray-500">sec</span>
                                    </div>
                                  )}
                                  {/* Cost ($) — required for EVERY file (images and videos) */}
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-500">$</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={costStr}
                                      onChange={(e) => {
                                        setFileCosts(prev => ({ ...prev, [idx]: e.target.value }));
                                        if (e.target.value !== '' && Number(e.target.value) >= 0) {
                                          setInvalidCostIndices(prev => {
                                            if (!prev.has(idx)) return prev;
                                            const next = new Set(prev);
                                            next.delete(idx);
                                            return next;
                                          });
                                        }
                                      }}
                                      placeholder="cost"
                                      required
                                      disabled={uploading}
                                      title="Cost in USD (locked actual cost)"
                                      className={`w-24 px-1.5 py-0.5 text-xs border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 ${invalidCostIndices.has(idx) ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'}`}
                                    />
                                  </div>
                                  <span className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Target Folder */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Upload to Subfolder (optional)
                    </label>
                    <input
                      type="text"
                      value={uploadTargetFolder}
                      onChange={(e) => setUploadTargetFolder(e.target.value)}
                      placeholder="e.g. Round 1, Final Cut, V2..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={uploading}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Leave empty to upload directly to the request folder
                    </p>
                  </div>

                  {/* Creative Metadata */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Creative Type
                      </label>
                      <select
                        value={creativeType}
                        onChange={(e) => setCreativeType(e.target.value)}
                        disabled={uploading}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select type...</option>
                        <option value="UGC">UGC</option>
                        <option value="STATIC">Static</option>
                        <option value="MOTION">Motion</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Creative Angle
                      </label>
                      <select
                        value={creativeAngle}
                        onChange={(e) => setCreativeAngle(e.target.value)}
                        disabled={uploading}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">None</option>
                        <option value="FEAR">Fear</option>
                        <option value="PRICE">Price</option>
                        <option value="TRUST">Trust</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Script Number
                      </label>
                      <input
                        type="text"
                        value={scriptNumber}
                        onChange={(e) => setScriptNumber(e.target.value)}
                        disabled={uploading}
                        placeholder="e.g., S3 or 3"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Comments */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Comments / Remarks (optional)
                    </label>
                    <textarea
                      value={uploadComments}
                      onChange={(e) => setUploadComments(e.target.value)}
                      placeholder="Add any notes about this upload..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      disabled={uploading}
                    />
                  </div>

                  {/* Error Message */}
                  {uploadError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <p className="text-sm text-red-800 dark:text-red-200">{uploadError}</p>
                    </div>
                  )}

                  {/* Success Message */}
                  {uploadSuccess && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <p className="text-sm text-green-800 dark:text-green-200">
                          File uploaded successfully!
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={selectedFiles.length === 0 || uploading}
                    >
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowUploadForm(false);
                        setSelectedFiles([]);
                        setFileDurations({});
                        setUploadComments('');
                        setUploadTargetFolder('');
                        setUploadError('');
                      }}
                      disabled={uploading}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Uploaded Files */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Uploaded Files ({filteredUploadsForSearch.length}{filteredUploadsForSearch.length !== request.uploads.length ? ` of ${request.uploads.length}` : ''})
                </h3>
                {/* View Mode Toggle */}
                <div className="flex items-center border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
                  {([['list', List, 'List'], ['grid', Grid3X3, 'Grid'], ['tile', LayoutGrid, 'Tiles']] as const).map(([mode, Icon, label]) => (
                    <button
                      key={mode}
                      onClick={() => setUploadViewMode(mode)}
                      title={label}
                      className={`p-1.5 transition-colors ${uploadViewMode === mode ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </button>
                  ))}
                </div>
              </div>

              {request.uploads.length > 0 && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={uploadSearchTerm}
                    onChange={(e) => setUploadSearchTerm(e.target.value)}
                    placeholder="Search files..."
                    className="h-7 px-2 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-blue-500 w-40"
                  />
                  <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedUploads.size === request.uploads.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    Select All
                  </label>

                  {selectedUploads.size > 0 && (
                    <>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({selectedUploads.size} selected)
                      </span>
                      {!readOnly && (() => {
                        // Disable button if a download for the EXACT same selected
                        // file set is already in flight. Prevents the repeated-click
                        // pattern that crashed the dyno (multiple ZIP jobs in parallel).
                        const selectedFileIds = request.uploads
                          .filter(u => selectedUploads.has(u.id))
                          .map(u => u.file_id)
                          .filter(Boolean) as string[];
                        const inFlight = isDownloadingFiles(selectedFileIds);
                        return (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleBulkDownload}
                            disabled={bulkActionInProgress || inFlight}
                            title={inFlight ? 'A download for this selection is already in progress' : undefined}
                          >
                            {inFlight ? (
                              <span className="flex items-center gap-1">
                                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                Downloading…
                              </span>
                            ) : 'Download Selected'}
                          </Button>
                        );
                      })()}
                      {!readOnly && user?.role !== 'creative' && (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={handleBulkAddToLibrary}
                          disabled={bulkActionInProgress}
                        >
                          Add Selected to Library
                        </Button>
                      )}
                      {/* Bulk delete is restricted to admin / creative_head / assistant_team_lead.
                          Regular editors can only delete their own uploads via the per-file menu. */}
                      {(() => {
                        const allRoles = [user?.role, ...((user as any)?.additional_roles || [])].filter(Boolean) as string[];
                        const canBulkDelete = allRoles.some(r =>
                          ['admin','creative_head','assistant_team_lead','ceo','head_media_buying'].includes(r)
                        );
                        if (!canBulkDelete) return null;
                        return (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={handleBulkRemoveFromRequest}
                            disabled={bulkActionInProgress}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete ({selectedUploads.size})
                          </Button>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}
            </div>

            {request.uploads.length > 0 ? (
              <FileRequestOrganizer
                requestId={requestId}
                uploads={filteredUploadsForSearch}
                canOrganize={!!(user && (
                  isAdminRole(user.role, (user as any)?.additional_roles) ||
                  (user.role === 'creative' && ((user as any).additional_roles || []).some((r: string) =>
                    ['admin', 'assistant_team_lead', 'vertical_head'].includes(r)
                  )) ||
                  request.auto_assigned_head === user.id
                ))}
                userRole={user?.role}
                viewMode={uploadViewMode}
                selectedUploads={selectedUploads}
                onToggleSelect={toggleSelectUpload}
                onBatchSelect={(ids: string[]) => {
                  const newSelected = new Set(selectedUploads);
                  ids.forEach(id => newSelected.add(id));
                  setSelectedUploads(newSelected);
                }}
                onDownload={readOnly ? (() => {}) : handleDownload}
                onAddToLibrary={!readOnly && user?.role !== 'creative' ? handleAddToLibrary : undefined}
                onRemoveFromRequest={!readOnly && user?.role === 'creative' ? handleRemoveFromRequest : undefined}
                onRename={readOnly ? (() => {}) : handleRename}
                onRefresh={fetchRequestDetails}
              />
            ) : (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <p className="text-sm text-muted-foreground">No files uploaded yet</p>
              </div>
            )}
          </div>

          {/* Upload History - Single Accordion */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowUploadHistory(!showUploadHistory)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
            >
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Upload History
              </h3>
              {showUploadHistory ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )}
            </button>
            {showUploadHistory && (
              <div className="p-3 bg-white dark:bg-gray-900">
                <UploadHistoryTimeline requestId={requestId} />
              </div>
            )}
          </div>

          {/* Edit History Timeline */}
          {editHistory.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Edit History
              </h3>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-3">
                {editHistory.map((edit: any) => (
                  <div key={edit.id} className="border-l-4 border-blue-500 pl-4 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {edit.edited_by_name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(edit.edited_at)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                      {Object.keys(edit.changes).map((field: string) => (
                        <div key={field}>
                          <span className="font-medium capitalize">{field.replace(/_/g, ' ')}:</span>
                          <span className="text-red-600 dark:text-red-400 line-through ml-2">
                            {JSON.stringify(edit.previous_values[field])}
                          </span>
                          <span className="text-green-600 dark:text-green-400 ml-2">
                            {JSON.stringify(edit.changes[field])}
                          </span>
                        </div>
                      ))}
                      {edit.edit_reason && (
                        <div className="mt-2 italic text-gray-500">
                          Reason: {edit.edit_reason}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 justify-end p-6 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {/* Reassignment Modal - multi-editor with creative distribution */}
      {showMarkProgressModal && request && (
        <MarkProgressModal
          requestId={requestId}
          requestTitle={request.title}
          editors={(request.assigned_editors || []).filter(e => e.status !== 'reassigned' && e.status !== 'removed').map(e => ({
            id: e.id,
            name: e.name,
            display_name: e.display_name,
            status: e.status,
            num_creatives_assigned: e.num_creatives_assigned,
            actual_uploads: e.actual_uploads,
            creatives_completed: e.creatives_completed,
            deliverables_uploaded: e.deliverables_uploaded,
          }))}
          onClose={() => setShowMarkProgressModal(false)}
          onUpdate={() => {
            setShowMarkProgressModal(false);
            fetchRequestDetails();
          }}
        />
      )}

      {showReassignModal && request && (
        <ReassignFileRequestModal
          requestId={requestId}
          requestTitle={request.title}
          currentEditors={
            request.assigned_editors
              ? request.assigned_editors
                  .filter(e => e.status !== 'reassigned' && e.status !== 'removed')
                  .map(e => ({ id: e.id, name: e.display_name || e.name }))
              : []
          }
          numCreatives={request.num_creatives_requested || 0}
          onClose={() => setShowReassignModal(false)}
          onSuccess={handleReassignSuccess}
        />
      )}

      {/* 3-Step Canvas Brief Modal */}
      {show3StepCanvas && (
        <CanvasBrief3Step
          requestId={requestId}
          initialContent={
            canvas?.content
              ? typeof canvas.content === 'string'
                ? canvas.content
                : JSON.stringify(canvas.content)
              : undefined
          }
          onSave={handleSave3StepCanvas}
          onClose={() => setShow3StepCanvas(false)}
          readOnly={user?.role === 'creative'}
        />
      )}

      {/* Duplicate Request Modal - Opens CreateFileRequestModal pre-filled */}
      {showDuplicateModal && request && (
        <CreateFileRequestModal
          onClose={() => setShowDuplicateModal(false)}
          onSuccess={() => {
            setShowDuplicateModal(false);
            onUpdate();
            onClose();
          }}
          initialData={{
            requestType: request.request_type || '',
            platforms: request.platforms || (request.platform ? [request.platform] : []),
            verticals: request.verticals || (request.vertical ? [request.vertical] : []),
            conceptNotes: request.description || '',
            numCreatives: request.num_creatives_requested ? String(request.num_creatives_requested) : '',
            allowMultipleUploads: request.allow_multiple_uploads,
            requireEmail: request.require_email,
            customMessage: request.custom_message || '',
            assignedBuyerIds: request.assigned_buyer_ids || (request.assigned_buyer_id ? [request.assigned_buyer_id] : []),
            canvasHeadline: canvas?.content?.headline || '',
            canvasScript: canvas?.content?.script || '',
          }}
        />
      )}
      {/* Download status toast */}
      {downloadStatus.active && (
        <div className={`fixed bottom-4 right-4 z-[60] px-5 py-3 rounded-lg shadow-xl text-sm flex items-center gap-3 max-w-md ${
          downloadStatus.type === 'error' ? 'bg-red-600 text-white' :
          downloadStatus.type === 'success' ? 'bg-green-600 text-white' :
          'bg-blue-600 text-white'
        }`}>
          {downloadStatus.type === 'preparing' && (
            <svg className="animate-spin h-5 w-5 flex-shrink-0" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {downloadStatus.type === 'success' && (
            <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          )}
          {downloadStatus.type === 'error' && (
            <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          )}
          <span>{downloadStatus.message}</span>
          <button onClick={() => setDownloadStatus(prev => ({ ...prev, active: false }))} className="ml-2 opacity-70 hover:opacity-100">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Script Viewer Modal - Read-only view of script database entries */}
      {scriptModal && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 pt-8 pb-4 px-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-3xl">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 shrink-0">
                {scriptModal.script_number}
              </span>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white flex-1">{scriptModal.script_title}</h2>
              <button
                onClick={() => { setScriptModal(null); setSelectedScriptCode(null); }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
              {scriptModal.content && (
                <div className="prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: scriptModal.content }} />
              )}
              {!scriptModal.content && (
                <p className="text-sm text-gray-500 dark:text-gray-400">No content</p>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => { setScriptModal(null); setSelectedScriptCode(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk download with UCODE — per-file Normal/Flex selection */}
      {bulkCodeModalState.isOpen && (
        <BulkDownloadCodeModal
          files={bulkCodeModalState.files}
          folderMap={bulkCodeModalState.folderMap}
          label={bulkCodeModalState.label}
          onClose={() => setBulkCodeModalState(s => ({ ...s, isOpen: false }))}
          onReady={(filenameOverrides, folderMap) => {
            setBulkCodeModalState(s => ({ ...s, isOpen: false }));
            startDownload({
              fileIds: bulkCodeModalState.files.map(f => f.file_id),
              folderMap: folderMap,
              filenameOverrides,
              label: bulkCodeModalState.label,
            });
          }}
        />
      )}

      {/* Bulk download pre-flight confirmation — shows count + size + ETA */}
      <BulkDownloadConfirmModal
        isOpen={bulkDownloadModalState.isOpen}
        onClose={() => setBulkDownloadModalState(s => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          startDownload({
            fileIds: bulkDownloadModalState.fileIds,
            folderMap: bulkDownloadModalState.folderMap,
            label: bulkDownloadModalState.label,
          });
          setBulkDownloadModalState(s => ({ ...s, isOpen: false }));
        }}
        fileIds={bulkDownloadModalState.fileIds}
        context={bulkDownloadModalState.label}
      />

      {/* Delete confirmation modal — single + bulk paths converge here */}
      <DeleteConfirmModal
        isOpen={!!deleteModal}
        onClose={() => !bulkActionInProgress && setDeleteModal(null)}
        onConfirm={() => {
          if (deleteModal?.kind === 'single') return confirmDeleteSingle();
          if (deleteModal?.kind === 'bulk') return confirmDeleteBulk();
        }}
        title={deleteModal?.kind === 'bulk' ? `Delete ${deleteModal.uploads.length} files` : 'Delete this upload'}
        body={
          deleteModal?.kind === 'bulk' ? (
            <div className="space-y-2">
              <p className="text-foreground">
                You're about to permanently delete <strong>{deleteModal.uploads.length}</strong> upload{deleteModal.uploads.length > 1 ? 's' : ''} from this request.
              </p>
              <p className="text-xs text-muted-foreground">
                Some of these uploads may belong to other editors. They will no longer see them.
              </p>
              <div className="max-h-32 overflow-y-auto bg-muted/40 rounded p-2 text-xs space-y-0.5">
                {deleteModal.uploads.slice(0, 6).map((u: any) => (
                  <div key={u.id} className="truncate">• {u.original_filename || u.filename}</div>
                ))}
                {deleteModal.uploads.length > 6 && (
                  <div className="text-muted-foreground">…and {deleteModal.uploads.length - 6} more</div>
                )}
              </div>
            </div>
          ) : deleteModal?.kind === 'single' ? (
            <p>
              Delete <span className="font-mono">{deleteModal.upload.original_filename || deleteModal.upload.filename}</span>?
            </p>
          ) : null
        }
        requireTypedConfirmation={deleteModal?.kind === 'bulk'}
        confirmLabel={deleteModal?.kind === 'bulk' ? `Delete ${deleteModal.uploads.length} files` : 'Delete'}
        busy={bulkActionInProgress}
      />

      {downloadCodeFile && (
        <DownloadCodeModal
          mediaFileId={downloadCodeFile.file_id}
          filename={downloadCodeFile.original_filename}
          onClose={() => setDownloadCodeFile(null)}
          onDownload={(downloadUrl, downloadFilename) => {
            setDownloadCodeFile(null);
            performDirectDownload(downloadUrl, downloadFilename);
          }}
        />
      )}

      {/* LEGACY cost step — these uploads predate the per-file cost feature, so their cost was never
          captured. Collect a cost per delivered file before launching/closing. Reuses the same
          per-file duration + cost boxes used at upload. */}
      {costStep && request && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => !legacyCostSubmitting && setCostStep(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Enter the cost before {costStep.action === 'launch' ? 'launching' : 'closing'}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                These files were uploaded before per-file costs were tracked. Enter the cost (USD) for each
                file. Enter 0 if nothing was spent.
              </p>
            </div>
            <div className="px-4 py-3 overflow-y-auto space-y-1.5">
              {(request.uploads || []).map((u: any) => {
                const isVideo = isVideoUpload(u);
                const needsDuration = isVideo && !(Number(u.duration) > 0);
                return (
                  <div key={u.id} className="flex items-center justify-between gap-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1.5 rounded">
                    <span className="truncate min-w-0" title={u.original_filename}>{u.original_filename}</span>
                    <div className="flex items-center gap-2 ml-2 whitespace-nowrap">
                      {needsDuration && (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={legacyFileDurations[u.id] || ''}
                            onChange={(e) => setLegacyFileDurations(prev => ({ ...prev, [u.id]: e.target.value }))}
                            disabled={legacyCostSubmitting}
                            title="Duration in seconds"
                            className="w-20 px-1.5 py-0.5 text-xs border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-500">sec</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={legacyFileCosts[u.id] || ''}
                          onChange={(e) => setLegacyFileCosts(prev => ({ ...prev, [u.id]: e.target.value }))}
                          placeholder="cost"
                          disabled={legacyCostSubmitting}
                          title="Cost in USD"
                          className="w-24 px-1.5 py-0.5 text-xs border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {legacyCostError && (
              <div className="px-4 pb-1 text-xs text-red-500">{legacyCostError}</div>
            )}
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCostStep(null)}
                disabled={legacyCostSubmitting}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitLegacyCost}
                disabled={legacyCostSubmitting}
                className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {legacyCostSubmitting ? 'Saving…' : `Save & ${costStep.action === 'launch' ? 'Launch' : 'Close'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
